import React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { BANDS_BY_SCALE, getScoreBand } from '@src/constants/insights';

const scaleType = PropTypes.oneOf(['quality', 'signal']);

// scale: 'quality' (higher is better) or 'signal' (how strongly something shows), which only changes the bands.
export const VpScoreRing = ({ score, size = 64, scale = 'quality' }) => {
  const band = getScoreBand(score, scale);
  return (
    <Box sx={{ position: 'relative', display: 'inline-flex', width: size, height: size, flexShrink: 0 }}>
      <CircularProgress variant="determinate" value={100} size={size} thickness={3} sx={{ color: 'grey.300', position: 'absolute' }} />
      <CircularProgress variant="determinate" value={score ?? 0} size={size} thickness={3} sx={{ color: band ? band.color : 'primary.main' }} />
      <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <Typography sx={{ fontWeight: 700, lineHeight: 1, fontSize: size * 0.3 }}>{score ?? '—'}</Typography>
        {score !== null && score !== undefined && <Typography sx={{ color: 'text.secondary', lineHeight: 1, fontSize: size * 0.16 }}>/100</Typography>}
      </Box>
    </Box>
  );
};

VpScoreRing.propTypes = { score: PropTypes.number, size: PropTypes.number, scale: scaleType };

export const VpScoreBadge = ({ score, scale = 'quality' }) => {
  const band = getScoreBand(score, scale);
  if (!band) return null;
  return (
    <Box
      component="span"
      sx={{ display: 'inline-block', px: 1.6, py: 0.4, borderRadius: '10px', fontSize: 12, fontWeight: 600, lineHeight: 1.5, color: band.textColor, bgcolor: band.background, whiteSpace: 'nowrap' }}
    >
      {band.label}
    </Box>
  );
};

VpScoreBadge.propTypes = { score: PropTypes.number, scale: scaleType };

export const VpOverallScore = ({ score, scale = 'quality' }) => (
  <Stack alignItems="center" spacing={0.8}>
    <Typography component="span" sx={{ fontSize: 28, fontWeight: 700, lineHeight: 1, color: score === null || score === undefined ? 'text.secondary' : 'text.primary' }}>
      {score ?? '—'}
    </Typography>
    <VpScoreBadge score={score} scale={scale} />
  </Stack>
);

VpOverallScore.propTypes = { score: PropTypes.number, scale: scaleType };

// Read by screen readers, not shown.
export const visuallyHidden = { position: 'absolute', width: '1px', height: '1px', p: 0, m: '-1px', overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0 };

const NOT_EVALUATED_HINT = 'Not evaluated with this template';
export const NOT_APPLICABLE_HINT = 'Evaluated, but nothing in the answer applied to this template';

// A template's score in a dense list: the number on its band's tint, "N/A" when the video was
// evaluated but nothing applied, or a dash when it was never evaluated with that template. label names
// the template for screen readers where the visible column header is not read with the cell.
export const VpScoreCell = ({ score, notApplicable = false, label, scale = 'quality' }) => {
  const band = getScoreBand(score, scale);
  const visible = band ? score : notApplicable ? 'N/A' : '—';
  const spoken = band ? `${score}, ${band.label}` : notApplicable ? 'not applicable' : 'not evaluated';
  const cell = (
    <Box
      component="span"
      sx={{
        display: 'inline-block',
        minWidth: 44,
        px: 1.6,
        py: 0.8,
        boxSizing: 'border-box',
        borderRadius: '5px',
        textAlign: 'center',
        fontSize: 14,
        fontWeight: 600,
        lineHeight: 1.4,
        color: band ? band.textColor : 'text.secondary',
        bgcolor: band ? band.background : 'vpColors.greyLighter'
      }}
    >
      {label ? (
        <>
          <span aria-hidden="true">{visible}</span>
          <Box component="span" sx={visuallyHidden}>{`${label}: ${spoken}`}</Box>
        </>
      ) : visible}
    </Box>
  );
  // describeChild: the hint describes the cell; as a label it would replace the template name.
  return band ? cell : <Tooltip describeChild title={notApplicable ? NOT_APPLICABLE_HINT : NOT_EVALUATED_HINT}>{cell}</Tooltip>;
};

VpScoreCell.propTypes = { score: PropTypes.number, notApplicable: PropTypes.bool, label: PropTypes.string, scale: scaleType };

// scales: the scales on screen, so a page with quality and signal scores explains both sets of bands.
export const VpScoreLegend = ({ scales = ['quality'], children }) => (
  <Stack direction="row" flexWrap="wrap" alignItems="center" columnGap={3.6} rowGap={1} sx={{ fontSize: 12, color: 'text.secondary' }}>
    {scales.flatMap((scale) => BANDS_BY_SCALE[scale] || []).map((band) => (
      <Stack key={band.label} direction="row" alignItems="center" spacing={1.2} component="span">
        <Box component="span" sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: band.color }} />
        <span>{band.label} {band.range}</span>
      </Stack>
    ))}
    {children}
  </Stack>
);

VpScoreLegend.propTypes = { scales: PropTypes.arrayOf(scaleType), children: PropTypes.node };



// WEBPACK FOOTER //
// ./src/vp-ui/components/VpScoreRing/VpScoreRing.jsx